/** @jsx jsx */
import { DottedList, DottedListItem, Flex, InformationFillIcon, Paragraph } from "@eworkit/iris";
import { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { LazyUserAvatar } from "@/components/avatars";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { fetchJobApplication } from "@/data/job-applications";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { formatMultipleLocations } from "@/data/locations";
import { formatFullName } from "@/data/persons";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useToasts } from "@/views/components/Toast/useToasts";

import { useApplyWizard } from "../_components/ApplyWizardContext";

interface ApplyCandidatesListCardProps {
  profile: IConsultantProfile;
  jobRequest: JobRequest;
}

export const ApplyCandidatesListCard: FC<ApplyCandidatesListCardProps> = ({ profile, jobRequest }) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();

  const { contextConsultantProfileId, contextCompany } = useContext(UserContext);
  const { addToast } = useToasts();
  const { sendEvent } = useAnalytics();

  const { setProfile, setDraft, goToNextStep, resetSavingState } = useApplyWizard();

  const handleClick = useCallback(async () => {
    try {
      if (profile.draftApplicationId) {
        const draft = await fetchJobApplication({
          jobApplicationId: profile.draftApplicationId,
          companyId: contextCompany?.id,
        });

        setDraft(draft);
      }

      setProfile(profile);
      resetSavingState();
      goToNextStep();

      sendEvent({
        type: "apply_job_select_candidate",
        id: jobRequest.id,
        name: jobRequest.title!,
        location: formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length),
      });
    } catch (error) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    }
  }, [
    profile,
    setProfile,
    setDraft,
    goToNextStep,
    contextCompany,
    addToast,
    t,
    resetSavingState,
    sendEvent,
    jobRequest,
  ]);

  return (
    <ResponsiveCard
      as="button"
      breakpoint="md"
      onClick={handleClick}
      sx={{
        px: ["sm", "sm", "md", "md"],
        py: ["sm", "sm", "sm", "sm"],
        borderTopWidth: [0, null, 1],
        font: "inherit",
      }}>
      <Flex flexDirection={["column", null, "row"]} gap="xs">
        <LazyUserAvatar user={profile} />

        <Flex flexDirection="column" gap="sm">
          <Flex flexDirection="column" alignItems="flex-start">
            <Paragraph bold>
              {formatFullName(profile)}
              {profile.id === contextConsultantProfileId && ` (${t("JOB_REQUESTS.APPLY.WIZARD.YOU")})`}
            </Paragraph>
            <DottedList responsive>
              {profile.position?.name && (
                <DottedListItem sx={{ maxWidth: "100%" }}>
                  <Paragraph color="n700" ellipsis>
                    {profile.position.name}
                  </Paragraph>
                </DottedListItem>
              )}
              <DottedListItem sx={{ maxWidth: "100%" }}>
                <Paragraph color="n700" ellipsis>
                  {profile.email}
                </Paragraph>
              </DottedListItem>
            </DottedList>
          </Flex>

          {(profile.applicationCount >= 1 || profile.draftApplicationId) && (
            <Flex gap="xxs" alignItems="center">
              <InformationFillIcon color="b500" size="sm" />
              <Paragraph>
                {profile.draftApplicationId
                  ? t("JOB_REQUESTS.APPLY.WIZARD.DRAFT_INFO")
                  : profile.applicationCount === 1
                  ? t("JOB_REQUESTS.APPLY.WIZARD.ONE_APPLICATION_INFO", {
                      status: tCodelist("job-request-application-statuses", profile.firstApplicationStatus || ""),
                    })
                  : t("JOB_REQUESTS.APPLY.WIZARD.MANY_APPLICATIONS_INFO", {
                      count: profile.applicationCount,
                    })}
              </Paragraph>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ResponsiveCard>
  );
};
